import { useState, type CSSProperties } from 'react';

import { avatarSwatchClass } from '@/components/ui/avatarSwatch';
import { cn } from '@/lib/utils';

import { isHttpsUrl } from '../urls';

export type AvatarShape = 'rounded' | 'circle';

// 'two' names a person (Message, Person); 'one' names a chip recipient
export type AvatarInitialsCount = 'one' | 'two';

// An avatar is sized from the text beside it, never from a fixed default, so callers
// always pass `size`.
const SHAPE_CLASS: Record<AvatarShape, string> = {
  rounded: 'rounded-md',
  circle: 'rounded-full',
};

// One initial for a single name, else the first one or two parts' initials (an
// email address splits at its dots and @, so its first part is its local part)
function initialsOf(name: string, count: AvatarInitialsCount): string {
  const parts = name
    .trim()
    .split(/[\s@._+-]+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0].charAt(0);
  if (count === 'one') return first.toUpperCase();
  const second = parts.length > 1 ? parts[1].charAt(0) : '';
  return (first + second).toUpperCase();
}

const initialsFontSize = (size: number): number =>
  Math.max(8, Math.round(size * 0.4));

// Sizes are card units, passed as custom properties a class multiplies out; a menu
// outside the card's scale falls back to 1px a unit
const avatarSizeStyle = (size: number) =>
  ({
    '--avatar-size': size,
    '--avatar-initials': initialsFontSize(size),
  }) as CSSProperties;

// A sample or stale url can stay at complete=false, naturalWidth=0 with neither
// load nor error firing. So the initials are always drawn as their own layer, not
// as a fallback the image replaces. Keying on `url` gives each url a fresh `loaded`
// state.
function AvatarImage({ url }: { url: string }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <img
      src={url}
      alt=""
      referrerPolicy="no-referrer"
      onLoad={() => setLoaded(true)}
      onError={() => setLoaded(false)}
      className={cn(
        'absolute inset-0 size-full object-cover transition-opacity',
        loaded ? 'opacity-100' : 'opacity-0'
      )}
    />
  );
}

export function Avatar({
  id,
  name,
  avatarUrl,
  shape,
  size,
  initials = 'two',
  title,
  className,
}: {
  id: string;
  name: string;
  avatarUrl?: string;
  shape: AvatarShape;
  size: number;
  initials?: AvatarInitialsCount;
  title?: string;
  className?: string;
}) {
  const url = isHttpsUrl(avatarUrl) ? avatarUrl : undefined;

  return (
    <span
      data-avatar
      className={cn(
        'relative inline-flex size-[calc(var(--avatar-size)*var(--ac-u,1px))] shrink-0 overflow-hidden',
        SHAPE_CLASS[shape],
        className
      )}
      title={title}
      style={avatarSizeStyle(size)}
    >
      <span
        aria-hidden="true"
        title={title ?? name}
        className={cn(
          'flex size-full select-none items-center justify-center text-[length:calc(var(--avatar-initials)*var(--ac-u,1px))] font-semibold uppercase leading-none',
          avatarSwatchClass(id),
          SHAPE_CLASS[shape]
        )}
      >
        {initialsOf(name, initials)}
      </span>
      {url ? <AvatarImage key={url} url={url} /> : null}
    </span>
  );
}
